import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolleyball, faTableTennisPaddleBall, faFutbol } from "@fortawesome/free-solid-svg-icons";
import { SeaWaves } from "@mynaui/icons-react";
import { CalendarX, ChevronLeft, ChevronRight, CircleDot, Sun, Target, X } from "lucide-react";
import moment from "moment";
import { ITournamentItem } from "../../models/ITournament";
import { Button, Card, EmptyState, IconButton, Select, Spinner, Tag } from "../../ui";
import "./styles.scss";

type TournamentCalendarProps = {
  loading: boolean;
  tournaments: ITournamentItem[];
  currentMonth: moment.Moment;
  onMonthChange: (nextMonth: moment.Moment) => void;
  onViewTournament: (tournament: ITournamentItem) => void;
};

const TournamentCalendar: React.FC<TournamentCalendarProps> = ({
  loading,
  tournaments,
  currentMonth,
  onMonthChange,
  onViewTournament,
}) => {
  const [selectedSport, setSelectedSport] = useState<string>("All");
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);

  const calendarDays = useMemo(() => {
    const start = currentMonth.clone().startOf("month").startOf("week");
    const end = currentMonth.clone().endOf("month").endOf("week");
    const days: moment.Moment[] = [];
    const day = start.clone();
    while (day.isSameOrBefore(end, "day")) {
      days.push(day.clone());
      day.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  const monthDays = useMemo(() => {
    return calendarDays.filter((day) => day.isSame(currentMonth, "month"));
  }, [calendarDays, currentMonth]);

  const availableSports = useMemo(() => {
    const uniqueSports = Array.from(
      new Set(tournaments.map((tournament) => tournament.type?.trim()).filter((type) => type && type.length > 0))
    ).sort();
    return ["All", ...uniqueSports];
  }, [tournaments]);

  const filteredTournaments = useMemo(() => {
    if (selectedSport === "All") {
      return tournaments;
    }
    return tournaments.filter((tournament) => tournament.type === selectedSport);
  }, [selectedSport, tournaments]);

  const getTournamentsForDay = (day: moment.Moment) => {
    const dayStr = day.format("YYYY-MM-DD");

    return filteredTournaments.filter((tournament) => {
      // Check if tournament has specificDates (from ITournament, but may not be in ITournamentItem)
      const tournamentWithDates = tournament as ITournamentItem & { specificDates?: string };

      // Always check for specificDates first - if they exist, use them exclusively
      if (tournamentWithDates.specificDates && tournamentWithDates.specificDates.trim() !== "") {
        // Parse specificDates string - can be comma-separated dates
        // Handle different possible formats: "2026-01-15,2026-01-22" or "2026-01-15, 2026-01-22"
        const dateStrings = tournamentWithDates.specificDates
          .split(",")
          .map((d) => d.trim())
          .filter((d) => d.length > 0);

        // Check if the current day matches any of the specific dates
        for (const dateStr of dateStrings) {
          // Try parsing with moment - it handles various date formats
          const parsedDate = moment(dateStr);
          if (parsedDate.isValid()) {
            const parsedDateStr = parsedDate.format("YYYY-MM-DD");
            if (parsedDateStr === dayStr) {
              return true;
            }
          }
        }
        // If specificDates exist but don't match, don't show on this day
        return false;
      }

      // If no specific dates, show only on the start date (not across the date range)
      const startDate = moment(tournament.startDate);
      if (!startDate.isValid()) {
        return false;
      }
      return startDate.format("YYYY-MM-DD") === dayStr;
    });
  };

  const getFilteredTournamentsForDay = (day: moment.Moment) => {
    return getTournamentsForDay(day);
  };

  const goToPreviousMonth = () => onMonthChange(currentMonth.clone().subtract(1, "month"));
  const goToNextMonth = () => onMonthChange(currentMonth.clone().add(1, "month"));
  const goToCurrentMonth = () => {
    const current = moment();
    onMonthChange(current.clone().startOf("month"));
    setSelectedDayKey(current.format("YYYY-MM-DD"));
  };

  const mobileRows = monthDays
    .map((day) => {
      const dayTournaments = getFilteredTournamentsForDay(day);
      if (dayTournaments.length === 0) {
        return null;
      }

      return {
        day,
        tournaments: dayTournaments,
      };
    })
    .filter(Boolean) as { day: moment.Moment; tournaments: ITournamentItem[] }[];

  const selectedDay = selectedDayKey ? moment(selectedDayKey, "YYYY-MM-DD") : null;
  const selectedDayTournaments = selectedDay ? getFilteredTournamentsForDay(selectedDay) : [];

  // Sport colour mapping - the brand's sport accents: padel blue, volleyball orange, beach volleyball yellow; the other
  // sports stay neutral.
  const getSportColor = (sportType: string | undefined): string => {
    if (!sportType) return "var(--subtle)";
    const normalizedType = sportType.toLowerCase().trim();
    if (normalizedType === "volleyball") return "var(--spark)"; // Orange
    if (normalizedType === "beach volleyball") return "var(--yellow)"; // Yellow
    if (normalizedType === "padel") return "var(--brand)"; // Blue
    return "var(--subtle)"; // Beach Tennis, Spikeball, Dodgeball, Beach Football and anything new: neutral
  };

  // Get lighter version of sport color for card backgrounds
  const getSportColorLight = (sportType: string | undefined): string => {
    if (!sportType) return "var(--alt)";
    const normalizedType = sportType.toLowerCase().trim();
    if (normalizedType === "volleyball") return "var(--spark-tint)"; // Light orange
    if (normalizedType === "beach volleyball") return "color-mix(in srgb, var(--yellow) 20%, var(--card))"; // Light yellow
    if (normalizedType === "padel") return "var(--brand-tint)"; // Light blue
    return "var(--alt)"; // Neutral
  };

  // Sport icon mapping
  const getSportIcon = (sportType: string | undefined) => {
    if (!sportType) return null;
    const normalizedType = sportType.toLowerCase().trim();
    if (normalizedType === "volleyball") {
      return <FontAwesomeIcon icon={faVolleyball} className="sport-icon" />;
    }
    if (normalizedType === "beach volleyball") {
      return <SeaWaves className="sport-icon" size={16} />;
    }
    if (normalizedType === "padel") {
      return <FontAwesomeIcon icon={faTableTennisPaddleBall} className="sport-icon" />;
    }
    if (normalizedType === "beach tennis") {
      return <Sun className="sport-icon" size={16} />;
    }
    if (normalizedType === "spikeball") {
      return <CircleDot className="sport-icon" size={16} />;
    }
    if (normalizedType === "dodgeball") {
      return <Target className="sport-icon" size={16} />;
    }
    if (normalizedType === "beach football") {
      return <FontAwesomeIcon icon={faFutbol} className="sport-icon" />;
    }
    return null;
  };

  // Get unique sports for legend
  const legendSports = useMemo(() => {
    const uniqueSports = Array.from(
      new Set(tournaments.map((t) => t.type?.trim()).filter((type) => type && type.length > 0))
    ).sort();
    return uniqueSports;
  }, [tournaments]);

  return (
    <Card className="tournament-calendar">
      {/* Header with navigation and filter */}
      <div className="calendar-controls">
        <div className="calendar-navigation">
          <IconButton label="Previous month" icon={<ChevronLeft />} onClick={goToPreviousMonth} className="nav-btn" />
          <div className="month-display">
            <span className="month-name">{currentMonth.format("MMMM").toUpperCase()}</span>
            <span className="month-year">{currentMonth.format("YYYY")}</span>
          </div>
          <IconButton label="Next month" icon={<ChevronRight />} onClick={goToNextMonth} className="nav-btn" />
        </div>
        <div className="calendar-actions">
          <Button variant="ghost" size="sm" className="today-btn" onClick={goToCurrentMonth}>
            TODAY
          </Button>
          <Select
            size="sm"
            className="sport-filter"
            value={selectedSport}
            onChange={(event) => setSelectedSport(event.target.value)}
            aria-label="Filter by sport"
          >
            {availableSports.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Legend */}
      {legendSports.length > 0 && (
        <div className="calendar-legend">
          <span className="calendar-legend-label">SPORTS:</span>
          <div className="legend-items">
            {legendSports.map((sport) => (
              <div key={sport} className="legend-item">
                <span
                  className="legend-color"
                  style={{
                    backgroundColor: getSportColorLight(sport),
                    borderColor: getSportColor(sport),
                  }}
                />
                <span className="legend-text">{sport.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar content wrapper */}
      <div className="calendar-content">
        {/* Loading overlay */}
        {loading && (
          <div className="calendar-loader">
            <Spinner size="lg" />
            <span className="loader-text">Loading tournaments...</span>
          </div>
        )}

        {/* Desktop: Month grid view */}
        <div className="calendar-grid-view">
          <div className="weekday-headers">
            {moment.weekdaysShort().map((weekday) => (
              <div key={weekday} className="weekday-header">
                {weekday.toUpperCase()}
              </div>
            ))}
          </div>
          <div className="days-grid">
            {calendarDays.map((day) => {
              const tournamentsForDay = getFilteredTournamentsForDay(day);
              const hasTournaments = tournamentsForDay.length > 0;
              const isToday = day.isSame(moment(), "day");
              const isOutsideMonth = !day.isSame(currentMonth, "month");
              const dayKey = day.format("YYYY-MM-DD");
              const isSelected = selectedDayKey === dayKey;
              const visibleTournaments = tournamentsForDay.slice(0, 2);
              const remainingCount = tournamentsForDay.length - 2;

              return (
                <div
                  key={dayKey}
                  className={`day-cell ${isToday ? "today" : ""} ${isOutsideMonth ? "outside-month" : ""} ${hasTournaments ? "has-events" : ""} ${isSelected ? "is-selected" : ""}`}
                  onClick={() => setSelectedDayKey(isSelected ? null : dayKey)}
                >
                  <div className="day-header">
                    <span className="day-num">{day.date()}</span>
                    {hasTournaments && tournamentsForDay.length > 1 && (
                      <span className="day-event-count" data-tooltip={`${tournamentsForDay.length} clash(es)`}>
                        {tournamentsForDay.length}
                      </span>
                    )}
                  </div>
                  {hasTournaments && (
                    <div className="day-events">
                      {visibleTournaments.map((tournament) => (
                        <div
                          key={tournament.id}
                          className="event-item"
                          data-tooltip={tournament.name}
                          style={{
                            backgroundColor: getSportColorLight(tournament.type),
                            borderColor: getSportColor(tournament.type),
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewTournament(tournament);
                          }}
                        >
                          {tournament.name}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <span className="event-more-badge">+{remainingCount} more</span>
                      )}
                    </div>
                  )}
                  {hasTournaments && (
                    <div className="day-indicators">
                      {Array.from(new Set(tournamentsForDay.map((t) => t.type))).map((sportType) => (
                        <span
                          key={sportType || "default"}
                          className="day-indicator"
                          style={{ backgroundColor: getSportColor(sportType) }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected day panel - shows on both desktop and mobile */}
        <div className={`calendar-day-details ${selectedDay ? "is-visible" : ""}`}>
          {selectedDay ? (
            <div className="day-details-panel">
              <div className="day-details-header">
                <span className="day-details-label">{selectedDay.format("dddd, MMMM D")}</span>
                <Tag tone="neutral" className="day-details-count">
                  {selectedDayTournaments.length} clash{selectedDayTournaments.length !== 1 ? "es" : ""}
                </Tag>
                <IconButton
                  className="day-details-close"
                  label="Close"
                  size="sm"
                  icon={<X />}
                  onClick={() => setSelectedDayKey(null)}
                />
              </div>
              {selectedDayTournaments.length > 0 ? (
                <div className="day-details-list">
                  {selectedDayTournaments.map((tournament) => (
                    <div
                      key={tournament.id}
                      className="day-details-card"
                      style={{
                        backgroundColor: getSportColorLight(tournament.type),
                        borderColor: getSportColor(tournament.type),
                      }}
                      onClick={() => onViewTournament(tournament)}
                    >
                      <span
                        className="sport-color-dot"
                        style={{ backgroundColor: getSportColor(tournament.type) }}
                      />
                      {getSportIcon(tournament.type)}
                      <span className="day-details-card-name">{tournament.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="day-details-empty">No clashes on this day.</div>
              )}
            </div>
          ) : null}
        </div>

        {/* Mobile: List view */}
        <div className="calendar-list-view">
          {loading ? (
            <div className="list-loader">
              <Spinner size="lg" />
            </div>
          ) : mobileRows.length > 0 ? (
            <div className="events-list">
              {mobileRows.map(({ day, tournaments: dayTournaments }) => (
                <div key={day.format("YYYY-MM-DD")} className="list-day-group">
                  <div className="list-day-header">
                    <span className="list-day-num">{day.format("DD")}</span>
                    <span className="list-day-name">{day.format("MMM")}</span>
                  </div>
                  <div className="list-day-events">
                    {dayTournaments.map((tournament) => (
                      <div
                        key={tournament.id}
                        className="list-event-card"
                        style={{
                          backgroundColor: getSportColorLight(tournament.type),
                          borderColor: getSportColor(tournament.type),
                        }}
                        onClick={() => onViewTournament(tournament)}
                      >
                        <span
                          className="sport-color-dot"
                          style={{ backgroundColor: getSportColor(tournament.type) }}
                        />
                        {getSportIcon(tournament.type)}
                        {tournament.name}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="list-empty">
              <p>No clashes scheduled this month.</p>
            </div>
          )}
        </div>

        {/* Empty state for desktop */}
        {!loading && tournaments.length === 0 && (
          <EmptyState compact className="calendar-empty" icon={<CalendarX />} title="No clashes scheduled yet." />
        )}
      </div>
    </Card>
  );
};

export default TournamentCalendar;
